import React from 'react';

import cx from 'classnames';

import useChannelGroupRolesQuery from 'lane-shared/hooks/useChannelGroupRolesQuery';
import { GroupRole } from 'lane-shared/types/GroupRole';

import Dropdown from '../form/Dropdown';
import ChannelSelectorButton from './ChannelSelectorButton';
import { useGroupRoleSelectorChannelState } from './useGroupRoleSelectorChannelState';

import styles from './GroupRoleSelector.scss';

type Props = {
  onGroupRoleSelected: (groupRole: GroupRole | null) => void;
  disabled?: boolean;
  includeWorkplaceMember?: boolean;
  showChannelSelector?: boolean;
  groupRoleId?: string | null;
  groupRoleChannelId?: string | null;
  enablePersistedGroupRoleChannelId?: boolean;
  placeholder?: string;
  channelId: string | undefined;
  className?: string;
  style?: React.CSSProperties;
  channelSelectorButtonStyle?: React.CSSProperties;
  isCardView?: boolean;
  disableChannelSelection?: boolean;
  error?: string[] | null;
};

export default function GroupRoleSelector({
  className,
  style,
  channelSelectorButtonStyle,
  disabled = false,
  channelId,
  groupRoleId = null,
  groupRoleChannelId = null,
  enablePersistedGroupRoleChannelId = false,
  placeholder = 'web.admin.content.draftContent.target.teamModal.selectTeam',
  showChannelSelector = true,
  includeWorkplaceMember = true,
  onGroupRoleSelected,
  isCardView = false,
  disableChannelSelection,
  error,
}: Props) {
  const { groupRole, displayChannelId, setSelectedChannelId } =
    useGroupRoleSelectorChannelState({
      groupRoleId,
      groupRoleChannelId,
      channelId,
      fallbackChannelId: channelId,
      enablePersistedGroupRoleChannelId,
    });

  const { groupRoles } = useChannelGroupRolesQuery({
    channelId: displayChannelId,
    includeWorkplaceMember,
  });

  return (
    <div
      className={cx(styles.GroupRoleSelector, className)}
      style={style}
      data-has-channel-selector={showChannelSelector}
    >
      {showChannelSelector && (
        <ChannelSelectorButton
          disabled={disableChannelSelection || disabled}
          className={styles.channelSelector}
          style={channelSelectorButtonStyle}
          channelId={displayChannelId}
          isCardView={isCardView}
          onChannelSelected={(channel: any) => {
            setSelectedChannelId(channel?._id);
            onGroupRoleSelected(null);
          }}
        />
      )}
      <Dropdown
        className={styles.dropDown}
        disabled={
          disabled ||
          (enablePersistedGroupRoleChannelId && !displayChannelId)
        }
        items={groupRoles.map(role => ({
          label: role.name,
          value: role._id,
        }))}
        invalid={Boolean(error)}
        errors={error}
        placeholder={placeholder}
        value={groupRole?._id}
        onValueChange={value => {
          const groupRole = groupRoles.find(
            groupRole => groupRole._id === value
          );

          setSelectedChannelId(groupRole?.channel?._id);
          onGroupRoleSelected(groupRole || null);
        }}
      />
    </div>
  );
}
